import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import { globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { Update, Details } from "../../services/package.services";

const EditPackage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [initialData, setInitialData] = useState({
        title: "",
        description: "",
        price_monthly: "",
        package_type: "",
        price_yearly: "",
        status: 1
    });

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        {
            title: t("Horoscope Packages"),
            url: "/admin/package/list/1",
        },
        { title: t("btn_edit"), url: "" },
    ];

    useEffect(() => {
        if (id) {
            globalLoader(true);
            Details(id)
                .then((response) => {
                    if (response.success) {
                        const data = response.data;
                        setInitialData({
                            title: data.title || "",
                            description: data.description || "",
                            price_monthly: data.price_monthly || "",
                            package_type: data.package_type || "",
                            price_yearly: data.price_yearly || "",
                            status: data.status
                        });
                    }
                    globalLoader(false);
                })
                .catch((error) => {
                    console.log("error fetching details", error);
                    globalLoader(false);
                });
        }
    }, [id]);

    const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Title is required"),
        package_type: Yup.string().trim().required("Package type is required"),
        description: Yup.string().trim().required("Description is required"),
        price_monthly: Yup.number()
            .typeError("Monthly price must be a number")
            .required("Monthly price is required")
            .min(0, "Price cannot be less than 0"),
        price_yearly: Yup.number()
            .typeError("Yearly price must be a number")
            .required("Yearly price is required")
            .min(0, "Price cannot be less than 0"),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialData,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            globalLoader(true);
            setSubmitted(true);

            const payload = {
                ...values,
                id: id
            };

            Update(payload)
                .then((response) => {
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                            navigate(`/admin/package/list/1`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    globalLoader(false);
                    setSubmitted(false);
                });
        },
    });

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <h6 className="main-content-label mb-3">
                            {t("Edit Horoscope Package")}
                        </h6>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="row row-sm">
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="title" className="text-left d-flex">
                                        {t("Title")} :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="title"
                                        id="title"
                                        type="text"
                                        className="form-control"
                                        placeholder={t("Enter package title")}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                    />
                                    {formik.errors.title && formik.touched.title && (
                                        <span className="text-danger d-flex text-left">{formik.errors.title}</span>
                                    )}
                                </div>

                                <div className="col-lg-6 form-group">
                                    <label htmlFor="price_monthly" className="text-left d-flex">
                                        {t("Monthly Price")} :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="price_monthly"
                                        id="price_monthly"
                                        type="text"
                                        className="form-control"
                                        placeholder={t("Enter monthly price")}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.price_monthly}
                                    />
                                    {formik.errors.price_monthly && formik.touched.price_monthly && (
                                        <span className="text-danger d-flex text-left">{formik.errors.price_monthly}</span>
                                    )}
                                </div>

                                <div className="col-lg-6 form-group">
                                    <label htmlFor="price_yearly" className="text-left d-flex">
                                        {t("Yearly Price")} :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="price_yearly"
                                        id="price_yearly"
                                        type="text"
                                        className="form-control"
                                        placeholder={t("Enter yearly price")}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.price_yearly}
                                    />
                                    {formik.errors.price_yearly && formik.touched.price_yearly && (
                                        <span className="text-danger d-flex text-left">{formik.errors.price_yearly}</span>
                                    )}
                                </div>
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="package_type" className="text-left d-flex">
                                        {t("Package Type")} :
                                    </label>
                                    <select
                                        name="package_type"
                                        id="package_type"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.package_type}
                                    >
                                        <option value="">{t("Select package type")}</option>
                                        <option value="horoscope">{t("Horoscope")}</option>
                                        <option value="kundli">{t("Kundli")}</option>
                                        <option value="baby_name">{t("Baby Name")}</option>
                                        <option value="other">{t("Other")}</option>
                                    </select>
                                </div>

                                <div className="col-lg-6 form-group">
                                    <label htmlFor="status" className="text-left d-flex">
                                        {t("Status")} :
                                    </label>
                                    <select
                                        name="status"
                                        id="status"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.status}
                                    >
                                        <option value={1}>{t("Active")}</option>
                                        <option value={0}>{t("Inactive")}</option>
                                    </select>
                                </div>

                                <div className="col-lg-12 form-group">
                                    <label htmlFor="description" className="text-left d-flex">
                                        {t("Description")} :<span className="requirestar">*</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        id="description"
                                        className="form-control"
                                        rows="4"
                                        placeholder={t("Enter package description")}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.description}
                                    ></textarea>
                                    {formik.errors.description && formik.touched.description && (
                                        <span className="text-danger d-flex text-left">{formik.errors.description}</span>
                                    )}
                                </div>

                                <div className="col-md-12">
                                    <button
                                        className="btn btn-main-primary signbtn mr-2"
                                        type="submit"
                                        disabled={submitted}
                                    >
                                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                        {t("btn_update")}
                                    </button>
                                    <button
                                        className="btn ripple btn-secondary"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                        {t("btn_cancel")}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditPackage;
